import { Theme, toHtmlColor } from './color'
import { Layout } from './layout'
import { create } from './lib/create'

export function createScore(container: HTMLElement, layout: Layout, theme: Theme, timeLimit: number) {
  let totalCount = 0
  let goodCount = 0
  let remainingTime = timeLimit
  let timerInterval: number | null = null
  let onTimeUp: (() => void) | null = null

  const getScoreText = () => {
    return `${goodCount} correct`
  }
  
  const getTimerText = () => {
    const minutes = Math.floor(remainingTime / 60)
    const seconds = remainingTime % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }
  
  const getAssessment = () => {
    let assessment = ''
    if (goodCount === totalCount && totalCount > 0) {
      assessment = `Perfect score!`
    } else if (goodCount >= totalCount * 0.8) {
      assessment = `Great job!`
    } else if (goodCount >= totalCount * 0.5) {
      assessment = `Good effort!`
    } else {
      assessment = `Keep practicing!`
    }
    return assessment
  }

  // Score display
  let scoreSpan = create('span', {
    style: {
      position: 'fixed',
      padding: '8px 20px',
      height: `${layout.scoreHeight}px`,
      backgroundColor: 'rgba(45, 61, 77, 0.9)',
      fontSize: `${layout.scoreHeight * 0.7}px`,
      fontWeight: '500',
      userSelect: 'none',
      borderRadius: '0 0 12px 0',
      color: '#f0f4f8',
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
      letterSpacing: '0.5px',
    },
    textContent: getScoreText(),
  })

  // Timer display
  let timerSpan = create('span', {
    style: {
      position: 'fixed',
      right: '0',
      padding: '8px 20px',
      height: `${layout.scoreHeight}px`,
      backgroundColor: 'rgba(45, 61, 77, 0.9)',
      fontSize: `${layout.scoreHeight * 0.7}px`,
      fontWeight: '600',
      userSelect: 'none',
      borderRadius: '0 0 0 12px',
      color: '#f0f4f8',
      fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
      letterSpacing: '0.5px',
    },
    textContent: getTimerText(),
  })

  container.appendChild(scoreSpan)
  container.appendChild(timerSpan)

  // Start the timer
  const startTimer = () => {
    timerInterval = window.setInterval(() => {
      remainingTime--
      timerSpan.textContent = getTimerText()
      
      // Change color when time is low
      if (remainingTime <= 10) {
        timerSpan.style.color = '#ef4444'
        timerSpan.style.fontWeight = '700'
      } else if (remainingTime <= 30) {
        timerSpan.style.color = '#f59e0b'
      }
      
      if (remainingTime <= 0) {
        stopTimer()
        if (onTimeUp) {
          onTimeUp()
        }
      }
    }, 1000)
  }
  
  const stopTimer = () => {
    if (timerInterval !== null) {
      clearInterval(timerInterval)
      timerInterval = null
    }
  }

  return {
    startTimer,
    stopTimer,
    setOnTimeUp: (callback: () => void) => {
      onTimeUp = callback
    },
    // Show that we're waiting for remaining trains
    showWaitingForTrains: () => {
      timerSpan.textContent = '0:00'
      timerSpan.style.color = '#f59e0b'
    },
    // Update the display to show remaining trains
    updateRemainingTrains: (count: number) => {
      if (count > 0) {
        timerSpan.textContent = `${count} train${count > 1 ? 's' : ''} left`
        timerSpan.style.color = '#f59e0b'
      }
    },
    // Show score to the user
    showScore: (isTimeUp: boolean = false) => {
      stopTimer()
      container.appendChild(
        create(
          'div',
          {
            style: {
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              fontSize: '60px',
              textAlign: 'center',
              padding: '30px 60px',
              backgroundColor: 'rgba(45, 61, 77, 0.95)',
              borderRadius: '20px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
              userSelect: 'none',
              color: '#f0f4f8',
              fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, Arial, sans-serif",
              zIndex: '1000',
            },
          },
          [
            create('div', { 
              textContent: 'Game Complete!',
              style: {
                fontSize: '36px',
                fontWeight: '300',
                marginBottom: '10px',
                color: '#94a3b8',
              }
            }),
            create('div', { 
              textContent: `${goodCount}`,
              style: {
                fontSize: '80px',
                fontWeight: '700',
                marginBottom: '5px',
                color: '#22c55e',
              }
            }),
            create('div', { 
              textContent: `trains delivered correctly`,
              style: {
                fontSize: '20px',
                fontWeight: '400',
                marginBottom: '15px',
                color: '#94a3b8',
              }
            }),
            create('div', { 
              textContent: `${totalCount - goodCount} wrong`,
              style: {
                fontSize: '24px',
                fontWeight: '400',
                marginBottom: '10px',
                color: totalCount - goodCount > 0 ? '#ef4444' : '#22c55e',
              }
            }),
            create('div', { 
              textContent: getAssessment(),
              style: {
                fontSize: '28px',
                fontWeight: '500',
                color: '#60a5fa',
                marginTop: '10px',
              }
            }),
          ],
        ),
      )
    },
    scoreEvent(isGoodEvent: boolean) {
      totalCount += 1
      if (isGoodEvent) {
        goodCount += 1
      }
      scoreSpan.textContent = getScoreText()
    },
    getGoodCount: () => goodCount,
    getTotalCount: () => totalCount,
  }
}

export type Score = ReturnType<typeof createScore>
